import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api-client.config';
import { queryKeys } from '../react-query.config';
import { toast } from 'sonner';

export interface TenantLibraryImage {
  name: string;
  url: string;
  size: number;
  contentType: string | null;
  lastModified: string | null;
  etag: string | null;
}

export interface TenantImageLibraryResponse {
  tenantCode: string;
  path: string;
  images: TenantLibraryImage[];
  /**
   * False when this instance has no blob storage configured (CAE-676) — the
   * library is empty and uploads are unavailable. Distinct from a failed
   * request, so the UI can explain the state rather than report an error.
   */
  storageConfigured: boolean;
}

export interface UploadTenantImageRequest {
  filename: string;
  imageData: string;
}

export interface UploadTenantImageResponse {
  imageUrl: string;
  blobName: string;
  path: string;
  filename: string;
  message: string;
}

export interface DeleteTenantImageResponse {
  success: boolean;
  blobName: string;
  message: string;
}

export const tenantImageLibraryQueryKey = (tenantCode: string) =>
  [...queryKeys.tenants.detail(tenantCode), 'image-library'] as const;

export const getTenantImageLibrary = async (
  tenantCode: string
): Promise<TenantImageLibraryResponse> =>
  api.get(
    `/upload/tenant-images/${encodeURIComponent(tenantCode)}`
  ) as Promise<TenantImageLibraryResponse>;

export const uploadTenantImage = async (
  tenantCode: string,
  data: UploadTenantImageRequest
): Promise<UploadTenantImageResponse> =>
  api.post(
    `/upload/tenant-images/${encodeURIComponent(tenantCode)}`,
    data
  ) as Promise<UploadTenantImageResponse>;

export const deleteTenantImage = async (
  tenantCode: string,
  blobName: string
): Promise<DeleteTenantImageResponse> =>
  api.delete(`/upload/tenant-images/${encodeURIComponent(tenantCode)}`, {
    params: { blobName },
  }) as Promise<DeleteTenantImageResponse>;

export function useGetTenantImageLibraryQuery(tenantCode: string | undefined) {
  return useQuery({
    queryKey: tenantImageLibraryQueryKey(tenantCode || ''),
    queryFn: () => getTenantImageLibrary(tenantCode!),
    enabled: !!tenantCode,
  });
}

export function useUploadTenantImageMutation(tenantCode: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UploadTenantImageRequest) => uploadTenantImage(tenantCode, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantImageLibraryQueryKey(tenantCode),
      });
      toast.success('Image uploaded successfully');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to upload tenant image');
    },
  });
}

export function useDeleteTenantImageMutation(tenantCode: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (blobName: string) => deleteTenantImage(tenantCode, blobName),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantImageLibraryQueryKey(tenantCode),
      });
      toast.success('Image deleted');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to delete tenant image');
    },
  });
}
